// =============================================================================
//  Purpose   : lazy saga module loader
// =============================================================================
import { registerLoader } from 'uix/sagas/load';
import log from 'uix/logging';
import { size } from 'ldash';
const promises = [];
const loaded = [];
const criticalFailures = [];
const criticalImporters = [
    {
        name: 'config',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/config/sagas')
    },
    {
        name: 'i18n',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/i18n/sagas')
    },
    {
        name: 'network',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/network/sagas')
    },
    {
        name: 'auth',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/auth/sagas')
    },
    {
        name: 'templating',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/templating/sagas')
    },
    {
        name: 'sagas/extra',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/sagas/extra')
    }
];
const optionalImporters = [
    {
        name: 'db',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/db/sagas')
    },
    {
        name: 'notify',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/notify/sagas')
    },
    {
        name: 'states',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/states/sagas')
    },
    {
        name: 'job',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/job/sagas')
    },
    {
        name: 'upload',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/upload/sagas')
    },
    {
        name: 'wiki',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/wiki/sagas')
    },
    {
        name: 'widgets',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/widgets/sagas')
    },
    {
        name: 'viewer',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/viewer/sagas')
    },
    {
        name: 'tree',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/tree/sagas')
    },
    {
        name: 'modules/debug',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/modules/debug/sagas')
    },
    {
        name: 'modules/fulltext',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/modules/fulltext/sagas')
    },
    {
        name: 'modules/imports',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/modules/imports/sagas')
    },
    {
        name: 'modules/mods',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/modules/mods/sagas')
    },
    {
        name: 'modules/serverlog',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/modules/serverlog/sagas')
    },
    {
        name: 'modules/vlm',
        load: ()=>import(/* webpackChunkName: 'sagas' */ 'uix/modules/vlm/sagas')
    }
];
const getRegistries = ()=>require('uix/registries');
const registerModule = (module)=>{
    const registries = getRegistries();
    registries.registerSagas(module.sagas);
    if (module.runFilters && registries.uix?.templating) {
        registries.uix.templating.runFilters = module.runFilters;
    }
};
const notifyOptionalFailure = (name, error)=>{
    log.exception(`lazy saga import failed (optional: ${name})`, error);
    try {
        if (typeof vl !== 'undefined' && vl?.actions?.errorNotification) {
            vl.actions.errorNotification({
                header: `Saga-Load failed: ${name}`,
                error
            });
        }
        if (typeof vl !== 'undefined' && vl?.actions?.registerSagaError) {
            vl.actions.registerSagaError({
                code: 'SAGA_IMPORT_FAILED_OPTIONAL',
                message: `Optional saga import failed: ${name}`,
                url: `saga:${name}`
            });
        }
    } catch (notifyError) {
        log.exception('lazy saga notify failed', notifyError);
    }
};
const loadSagas = ()=>{
    criticalImporters.forEach(({ name, load })=>{
        let sagaName = name || 'unknown';
        let p = load().then(registerModule).catch((error)=>{
            criticalFailures.push({
                name: sagaName,
                error
            });
            log.exception(`lazy saga import failed (critical: ${sagaName})`, error);
        }).finally(()=>{
            loaded.push(true);
        });
        promises.push(p);
    });
    optionalImporters.forEach(({ name, load })=>{
        let sagaName = name || 'unknown';
        let p = load().then(registerModule).catch((error)=>{
            notifyOptionalFailure(sagaName, error);
        });
    });
};
const isReady = ()=>size(loaded) === size(promises);
const getCriticalFailures = ()=>criticalFailures;
const registerLazySagas = ()=>{
    registerLoader(loadSagas, isReady);
};
let registerNonChunkedSagas = ()=>{};
// #if process.env.UIX_PROD_CHUNKED !== 'true'
// registerNonChunkedSagas = () => {
//   const registries = getRegistries()
//   registries.registerSagas(require('uix/config/sagas').sagas)
//   registries.registerSagas(require('uix/db/sagas').sagas)
//   registries.registerSagas(require('uix/i18n/sagas').sagas)
//   registries.registerSagas(require('uix/network/sagas').sagas)
//   registries.registerSagas(require('uix/auth/sagas').sagas)
//   registries.registerSagas(require('uix/sagas/extra').sagas)
//   registries.registerSagas(require('uix/notify/sagas').sagas)
//   registries.registerSagas(require('uix/states/sagas').sagas)
//   registries.registerSagas(require('uix/job/sagas').sagas)
//   registries.registerSagas(require('uix/upload/sagas').sagas)
//   registries.registerSagas(require('uix/wiki/sagas').sagas)
//   registries.registerSagas(require('uix/widgets/sagas').sagas)
//   registries.registerSagas(require('uix/viewer/sagas').sagas)
//   registries.registerSagas(require('uix/tree/sagas').sagas)
//   const templating = require('uix/templating/sagas')
//   registries.registerSagas(templating.sagas)
//   if (templating.runFilters && registries.uix?.templating) {
//     registries.uix.templating.runFilters = templating.runFilters
//   }
//   registries.registerSagas(require('uix/modules/debug/sagas').sagas)
//   registries.registerSagas(require('uix/modules/fulltext/sagas').sagas)
//   registries.registerSagas(require('uix/modules/imports/sagas').sagas)
//   registries.registerSagas(require('uix/modules/mods/sagas').sagas)
//   registries.registerSagas(require('uix/modules/serverlog/sagas').sagas)
//   registries.registerSagas(require('uix/modules/vlm/sagas').sagas)
// }
// #endif
export { registerLazySagas, registerNonChunkedSagas, getCriticalFailures };
